import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { CreativeFilter, CreativeKind, CreativeSource } from '../../demo/types'

type Usage = NonNullable<CreativeFilter['usage']>

const sources = new Set<CreativeSource>(['ai', 'upload', 'kol'])
const kinds = new Set<CreativeKind>(['video', 'image'])
const usages = new Set<Exclude<Usage, 'all'>>(['used', 'unused'])

export function useCreativeFilter() {
  const [params, setParams] = useSearchParams()
  const sourceParam = params.get('source') as CreativeSource
  const kindParam = params.get('kind') as CreativeKind
  const usageParam = params.get('usage') as Exclude<Usage, 'all'>
  const source: CreativeSource | 'all' = sources.has(sourceParam) ? sourceParam : 'all'
  const kind: CreativeKind | 'all' = kinds.has(kindParam) ? kindParam : 'all'
  const usage: Usage = usages.has(usageParam) ? usageParam : 'all'
  const productId = params.get('product') || undefined
  const q = params.get('q')?.trim() ?? ''

  const update = useCallback((key: string, value?: string) => {
    setParams((current) => {
      const next = new URLSearchParams(current)
      if (!value || value === 'all') next.delete(key)
      else next.set(key, value)
      return next
    }, { replace: true })
  }, [setParams])

  const filter = useMemo<CreativeFilter>(() => ({ source, kind, usage, productId, q }), [kind, productId, q, source, usage])
  const reset = useCallback(() => {
    setParams((current) => {
      const next = new URLSearchParams(current)
      for (const key of ['source', 'kind', 'product', 'usage', 'q']) next.delete(key)
      return next
    }, { replace: true })
  }, [setParams])

  return {
    filter,
    source,
    kind,
    usage,
    productId,
    q,
    setSource: (value: CreativeSource | 'all') => update('source', value),
    setKind: (value: CreativeKind | 'all') => update('kind', value),
    setUsage: (value: Usage) => update('usage', value),
    setProductId: (value?: string) => update('product', value),
    setQ: (value: string) => update('q', value.trimStart()),
    reset,
    hasActive: source !== 'all' || kind !== 'all' || usage !== 'all' || Boolean(productId || q),
  }
}
